import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CloseIcon,
  Column,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Row,
  Text,
  TextWithTooltip,
  HtImage,
} from "@hightouch/htui";
import { AttachmentTypeTile } from "src/components/llm/components/attachment-type-tile";
import type { AttachmentCategory } from "src/components/llm/utils/attachment-presentation";

export type OverflowAttachment = {
  id: string;
  label: string;
  imageSrc?: string;
  /**
   * Type treatment for a row with no thumbnail. Callers classify from the
   * attachment itself; a non-file context reference leaves it unset and keeps
   * the neutral icon.
   */
  category?: AttachmentCategory;
  onPreview?: () => void;
  onRemove?: () => void;
};

export const AttachmentOverflow = ({
  items,
  onOpenChange,
}: {
  items: OverflowAttachment[];
  onOpenChange?: (open: boolean) => void;
}) => {
  useEffect(() => {
    if (items.length === 0) onOpenChange?.(false);
  }, [items.length, onOpenChange]);

  if (items.length === 0) return null;
  return (
    <Popover
      isLazy
      lazyBehavior="unmount"
      placement="top-end"
      onOpen={() => onOpenChange?.(true)}
      onClose={() => onOpenChange?.(false)}
    >
      <PopoverTrigger>
        <Button
          type="button"
          size="sm"
          variant="tertiary"
          aria-label={`View ${items.length} more attachments`}
        >
          +{items.length} more
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="sm" maxWidth="calc(100vw - 32px)" p={3}>
          <Text size="sm" fontWeight="medium">
            {items.length} more attachments
          </Text>
          <Column
            as="ul"
            listStyleType="none"
            m={0}
            p={0}
            mt={2}
            maxHeight="64"
            overflowY="auto"
            gap={1}
          >
            {items.map((item) => (
              <Row as="li" key={item.id} gap={2} align="center">
                {item.imageSrc ? (
                  <OverflowThumbnail item={item} />
                ) : (
                  <AttachmentTypeTile
                    category={item.category ?? "unknown"}
                    size="lg"
                  />
                )}
                <Box flex={1} minWidth={0}>
                  <Box
                    as="button"
                    type="button"
                    width="100%"
                    textAlign="left"
                    borderRadius="sm"
                    cursor="pointer"
                    _focusVisible={{
                      outline: "2px solid",
                      outlineColor: "primary.base",
                      outlineOffset: "2px",
                    }}
                    onClick={item.onPreview}
                  >
                    <TextWithTooltip>{item.label}</TextWithTooltip>
                  </Box>
                </Box>
                {item.onRemove && (
                  <IconButton
                    type="button"
                    icon={CloseIcon}
                    aria-label={`Remove ${item.label}`}
                    size="sm"
                    variant="tertiary"
                    onClick={item.onRemove}
                  />
                )}
              </Row>
            ))}
          </Column>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const OverflowThumbnail = ({ item }: { item: OverflowAttachment }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    // Unmount media outside the scroll area so opening the list does not decode every image.
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(entry?.isIntersecting ?? false),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <Box ref={ref} boxSize={8} flexShrink={0}>
      {visible && item.imageSrc && (
        <Popover
          trigger="hover"
          placement="right"
          isLazy
          lazyBehavior="unmount"
        >
          <PopoverTrigger>
            <Box
              as="button"
              type="button"
              aria-label={`Preview ${item.label}`}
              boxSize={8}
              borderRadius="md"
              overflow="hidden"
              onClick={item.onPreview}
            >
              <HtImage
                src={item.imageSrc}
                decorative
                boxSize="100%"
                objectFit="cover"
              />
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              p={2}
              width="64"
              maxWidth="calc(100vw - 32px)"
              pointerEvents="none"
            >
              <HtImage
                src={item.imageSrc}
                alt={item.label}
                width="100%"
                height="64"
                objectFit="contain"
              />
            </PopoverContent>
          </Portal>
        </Popover>
      )}
    </Box>
  );
};
